import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { OidcCallbackHandler, OidcLoginRedirect } from '@nohara-dxdev/react-oidc';
import { LazyLayout, LoadingScreen } from '@nohara-dxdev/react-core';

import { IS_DEV_ENVIRONMENT } from '@/config/env';
import { paths } from '@/config/paths';

import { default as OrgRoot, ErrorBoundary as OrgErrorBoundary } from './routes/org/root';
import AppRoot, { ErrorBoundary as AppErrorBoundary } from './routes/root';
import { OidcLogoutRedirect } from './oidc-logout-redirect';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

export const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: paths.auth.callback.path,
      element: (
        <OidcCallbackHandler
          successPath={paths.home.path}
          errorPath={paths.error.path}
          element={<LoadingScreen />}
        />
      ),
    },
    {
      path: paths.auth.login.path,
      element: <OidcLoginRedirect element={<LoadingScreen />} />,
    },
    {
      path: paths.auth.logout.path,
      element: <OidcLogoutRedirect />,
    },
    {
      path: paths.home.path,
      element: <AppRoot />,
      ErrorBoundary: AppErrorBoundary,
      children: [
        {
          path: '',
          element: <Navigate to={paths.org.root.path} />,
        },
        {
          path: paths.org.root.path,
          element: <OrgRoot />,
          ErrorBoundary: OrgErrorBoundary,
          children: [
            {
              path: paths.onboarding.path,
              HydrateFallback: LoadingScreen,
              lazy: () => import('./routes/org/onboarding').then(convert(queryClient)),
            },
            {
              path: paths.org.userProfile.path,
              HydrateFallback: LazyLayout,
              lazy: () => import('./routes/org/user').then(convert(queryClient)),
            },
            {
              path: paths.org.selectedOrg.path,
              children: [
                {
                  path: paths.org.dashboard.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/dashboard').then(convert(queryClient)),
                },
                {
                  path: paths.org.projects.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/projects').then(convert(queryClient)),
                },
                {
                  path: paths.org.project.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/project-detail').then(convert(queryClient)),
                },
                {
                  path: paths.org.projectNewCalculation.path,
                  HydrateFallback: LazyLayout,
                  lazy: () =>
                    import('./routes/org/project-new-calculation').then(convert(queryClient)),
                },
                {
                  path: paths.org.projectCalculationResult.path,
                  HydrateFallback: LazyLayout,
                  lazy: () =>
                    import('./routes/org/project-calculation-result').then(convert(queryClient)),
                },
                {
                  path: paths.org.projectCalculationContinue.path,
                  HydrateFallback: LazyLayout,
                  lazy: () =>
                    import('./routes/org/project-calculation-continue').then(convert(queryClient)),
                },
                {
                  path: paths.org.userProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.users.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/users').then(convert(queryClient)),
                },
                {
                  path: paths.org.roles.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/roles').then(convert(queryClient)),
                },
                {
                  path: paths.org.user.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/user').then(convert(queryClient)),
                },
                {
                  path: paths.org.orgProfile.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/org').then(convert(queryClient)),
                },
                {
                  path: paths.org.masterData.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/master-data').then(convert(queryClient)),
                },
                {
                  path: paths.org.projectNew.path,
                  HydrateFallback: LazyLayout,
                  lazy: () => import('./routes/org/project-new').then(convert(queryClient)),
                },
                // dev-only tooling: excluded from the bundle outside local/dev builds
                ...(IS_DEV_ENVIRONMENT
                  ? [
                      {
                        path: paths.org.devAssignRole.path,
                        HydrateFallback: LazyLayout,
                        lazy: () =>
                          import('./routes/org/dev-assign-role').then(convert(queryClient)),
                      },
                    ]
                  : []),
              ],
            },
          ],
        },
      ],
    },
    {
      path: '*',
      HydrateFallback: LoadingScreen,
      lazy: () => import('./routes/not-found').then(convert(queryClient)),
    },
  ]);

export const AppRouter = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
